
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import aiDataApi from "@/services/api.aiData.service";
import settingsApi from '@/services/api.settings.service';
import apiKeyApi from "@/services/api.apiKey.service";
import { WorkerCounts, UpdateWorkerCountAction } from "@/store/virtual-operator/types";

const Eventqueue = namespace("eventqueue");

@Component({})
export default class VirtualOperatorsPopUp extends Vue {
	@Eventqueue.Mutation setVirtualOperatorControlPanelShown: (shown: boolean) => void;
	@Eventqueue.Getter("getVirtualOperatorControlPanelShown") virtualOperatorControlPanelShown!: boolean;
	@Getter getUserTenantGroupId: number;

	private errorMessage: string = "";
	private apiKey?: string | null = null;
    private tenantId?: string | null = null;

	private workerCountPoll: NodeJS.Timeout | null = null;
	private pollingInterval: number = 3000;
	private isUpdating: boolean = false;
	private isLoading: boolean = true;
	private isDestroyed: boolean = false;

	private workerCounts: WorkerCounts = {
		desiredWorkerCount: 0,
		maxWorkerCount: 0
	};

	private readonly VirtualOperatorApiKeyName: string = "VirtualOperator";

	public async mounted() : Promise<void> {
		var aiDataUrl = await settingsApi.getAiDataUrl();
        aiDataApi.setBaseApiUrl(aiDataUrl);

        this.apiKey = await apiKeyApi.getApiKey(this.VirtualOperatorApiKeyName);
        this.tenantId = this.getUserTenantGroupId.toString();
	}

	@Watch("virtualOperatorControlPanelShown")
	private async onVirtualOperatorControlPanelShownChanged(visible: boolean) : Promise<void> {
		if (visible) {
			this.isLoading = true;
			await this.fetchWorkerCounts();
			this.isLoading = false;
			this.startPolling();
		} else {
			this.errorMessage = "";
			this.stopPolling();
		}
	}

	private startPolling() : void {
		if (this.isDestroyed) {
			return;
		}

		this.workerCountPoll = setTimeout(async () => {
			await this.fetchWorkerCounts();
			this.startPolling();
		}, this.pollingInterval);
	}

	private async fetchWorkerCounts() : Promise<void> {
		try {
			this.workerCounts = await aiDataApi.getWorkerCounts(this.apiKey, this.tenantId);
			this.errorMessage = "";
		} catch (error) {
			this.errorMessage = "Failed to retrieve active virtual operator count.";
		}
	}

	private async handleUpdateWorkerIncrementClick() : Promise<void> {
		if (this.workerCounts.desiredWorkerCount >= this.workerCounts.maxWorkerCount) {
			this.errorMessage = "Cannot increase virtual operator count above maximum limit.";
			return;
		}

		await this.handleUpdateWorkerCountRequest(UpdateWorkerCountAction.Increment);
	}

	private async handleUpdateWorkerDecrementClick() : Promise<void> {
		if (this.workerCounts.desiredWorkerCount <= 0) {
			this.errorMessage = "Cannot decrease virtual operator count below minimum limit.";
			return;
		}

		await this.handleUpdateWorkerCountRequest(UpdateWorkerCountAction.Decrement);
	}

	private async handleUpdateWorkerStopClick() : Promise<void> {
		await this.handleUpdateWorkerCountRequest(UpdateWorkerCountAction.Reset);
	}

	private async handleUpdateWorkerCountRequest(action: UpdateWorkerCountAction): Promise<void> {
		this.errorMessage = "";
		this.isUpdating = true;

		try {
			await aiDataApi.updateWorkerCount(action, this.apiKey, this.tenantId);
			this.workerCounts = await aiDataApi.getWorkerCounts(this.apiKey, this.tenantId);
		} catch (error) {
			this.errorMessage = "Failed to update virtual operator worker count.";
		}

		this.isUpdating = false;
	}

	private cancel(): void {
		this.setVirtualOperatorControlPanelShown(false);
	}

	private stopPolling(): void {
		if (this.workerCountPoll) {
			clearTimeout(this.workerCountPoll);
			this.workerCountPoll = null;
		}
	}

	private beforeDestroy(): void {
		this.isDestroyed = true;
		this.stopPolling();
	}
}

